import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Search } from "lucide-react";
import { mapWordBrownAreaLabelToId } from "@/lib/caRatingAreas";

const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

type AreaRates = { label: string; rates: any[] };

export type ImportedAgeBandRatesResult = {
  ageBandRates: Record<string, string>;
  ratingAreaId?: number;
  ratingAreaLabel?: string;
  // WHY: PUT /api/plan-age-rates/bulk/:planId only scopes its write to ONE
  // rating area when both ratingAreaId AND effectiveStart are given
  // (planRatesRoutes.ts's versionScope) - without a date it falls back to
  // replacing every area for this client (the incident in
  // docs/RATES_TAB_BUGS_FOUND.md). Reuses this dialog's own "Effective Date"
  // field (already required to look up carriers/plans) instead of adding a
  // second date input just for the save.
  effectiveStart?: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (result: ImportedAgeBandRatesResult) => void;
  // Pre-fill from the plan already open behind this dialog, so the broker
  // isn't retyping the state/carrier/plan/date/area they're already looking
  // at. All optional - when omitted the dialog behaves exactly as before
  // (blank, manual search). ids are OUR internal ids (carriers.id, plans.id),
  // matched directly against /api/plan-source/carriers and .../plans, which
  // already expose them (see PlanRates.tsx's call site for where these come
  // from).
  initialState?: string;
  initialEffectiveDate?: string;
  initialCarrierId?: number;
  initialPlanId?: number;
  initialRatingAreaId?: number;
};

function normalizeAreaRatesFromPlanDetails(data: any): AreaRates[] {
  let areas: any[] = [];

  if (Array.isArray(data)) {
    const node = data[0];
    const planNode =
      Array.isArray(node?.Plans) && node.Plans.length ? node.Plans[0] : node;
    areas = planNode?.Areas || planNode?.areaRateTable || planNode?.AreaRateTable || [];
  } else if (data) {
    const planNode =
      Array.isArray(data?.Plans) && data.Plans.length ? data.Plans[0] : data;
    areas = planNode?.Areas || planNode?.areaRateTable || planNode?.AreaRateTable || [];
  }

  let normalized: AreaRates[] = [];
  if (Array.isArray(areas)) {
    normalized = areas
      .map((a: any, idx: number) => {
        const label =
          a?.AreaName || a?.areaName || a?.Name || a?.name || `Area ${idx + 1}`;
        const rates = a?.Rates || a?.rates || [];
        return { label: String(label), rates: Array.isArray(rates) ? rates : [] };
      })
      .filter((x) => x.rates.length > 0);
  }

  if (normalized.length === 0) {
    const r = Array.isArray(areas) ? areas : [];
    normalized = [{ label: "Rates", rates: r }];
  }

  return normalized;
}

function mapRatesToAgeBandRates(rates: any[]): Record<string, string> {
  const mapped: Record<string, string> = {};

  for (const r of rates || []) {
    const fromRaw = r.AgeFrom ?? r.ageFrom ?? r.Age ?? r.age;
    const toRaw = r.AgeTo ?? r.ageTo ?? null;
    const rangeRaw = r.AgeRange || r.ageRange;
    const premiumRaw = r.Premium ?? r.premium ?? r.Rate ?? r.rate;

    const premium = premiumRaw != null ? String(premiumRaw) : "";
    if (!premium || !Number.isFinite(Number.parseFloat(premium))) continue;

    let label = "";
    if (typeof rangeRaw === "string" && rangeRaw.trim().length) {
      label = rangeRaw.replace(/\s+/g, "");
    } else if (fromRaw != null && toRaw != null && String(toRaw) !== String(fromRaw)) {
      label = `${fromRaw}-${toRaw}`;
    } else if (
      fromRaw != null &&
      (toRaw == null || String(toRaw) === String(fromRaw)) &&
      Number(fromRaw) >= 64
    ) {
      label = `${fromRaw}+`;
    } else if (fromRaw != null) {
      label = String(fromRaw);
    }

    if (!label) continue;
    mapped[label] = premium;
  }

  return mapped;
}

function formatAgeLabel(r: any): string {
  const fromRaw = r.AgeFrom ?? r.ageFrom ?? r.Age ?? r.age;
  const toRaw = r.AgeTo ?? r.ageTo ?? null;
  const rangeRaw = r.AgeRange || r.ageRange;

  if (typeof rangeRaw === "string" && rangeRaw.trim().length) {
    return rangeRaw.replace(/\s+/g, "");
  }

  if (fromRaw != null && toRaw != null && String(toRaw) !== String(fromRaw)) {
    return `${fromRaw}-${toRaw}`;
  }

  if (
    fromRaw != null &&
    (toRaw == null || String(toRaw) === String(fromRaw)) &&
    Number(fromRaw) >= 64
  ) {
    return `${fromRaw}+`;
  }

  return fromRaw != null ? String(fromRaw) : "";
}

function formatPremium(r: any): string {
  const premiumRaw = r.Premium ?? r.premium ?? r.Rate ?? r.rate;
  if (premiumRaw == null) return "";
  const parsed = Number.parseFloat(String(premiumRaw));
  if (!Number.isFinite(parsed)) return "";
  return parsed.toFixed(2);
}

export function ImportRatesFromLibraryDialog({
  open,
  onOpenChange,
  onImport,
  initialState,
  initialEffectiveDate,
  initialCarrierId,
  initialPlanId,
  initialRatingAreaId,
}: Props) {
  const { toast } = useToast();

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);
  const [selectedState, setSelectedState] = useState<string>(initialState || "CA");
  const [effectiveDate, setEffectiveDate] = useState<string>(initialEffectiveDate || today);

  const [loadingCarriers, setLoadingCarriers] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [wbCarriers, setWbCarriers] = useState<any[]>([]);
  const [selectedCarrier, setSelectedCarrier] = useState<any | null>(null);
  const [wbPlans, setWbPlans] = useState<any[]>([]);

  const [planSearchTerm, setPlanSearchTerm] = useState<string>("");

  const [areaRates, setAreaRates] = useState<AreaRates[]>([]);
  const [selectedAreaIdx, setSelectedAreaIdx] = useState<number>(0);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPlan, setPreviewPlan] = useState<any | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const filteredPlans = useMemo(() => {
    if (!planSearchTerm.trim()) return wbPlans;
    const q = planSearchTerm.trim().toLowerCase();
    return wbPlans.filter((p: any) =>
      String(p?.name || "").toLowerCase().includes(q)
    );
  }, [wbPlans, planSearchTerm]);

  // fetchWbCarriers/fetchWbPlans/openPlanPreview all RETURN what they fetched
  // (not just setState) so the auto-fill effect below can chain them with
  // plain awaits, instead of reading component state right after setting it
  // - which would still see the pre-update value (stale-closure/batching).
  // stateOverride/dateOverride exist for the same reason: the auto-fill
  // effect calls setSelectedState/setEffectiveDate then immediately needs to
  // fetch WITH those new values in the same tick, before React re-renders.
  const fetchWbCarriers = async (stateOverride?: string, dateOverride?: string): Promise<any[]> => {
    setLoadingCarriers(true);
    setWbCarriers([]);
    setSelectedCarrier(null);
    setWbPlans([]);
    setAreaRates([]);
    setPreviewOpen(false);
    setPreviewPlan(null);

    try {
      const response = await apiRequest(
        "GET",
        `/api/plan-source/carriers?state=${stateOverride ?? selectedState}&effectiveDate=${dateOverride ?? effectiveDate}`,
        undefined
      );
      const data = await response.json();

      let list: any[] = [];
      if (data?.success && Array.isArray(data?.carriers)) {
        list = data.carriers;
      } else if (data?.success && Array.isArray(data?.carriers?.Carriers)) {
        list = data.carriers.Carriers;
      } else if (Array.isArray(data)) {
        list = data;
      } else {
        throw new Error("Invalid carriers response");
      }
      setWbCarriers(list);
      return list;
    } catch (err: any) {
      toast({
        title: "Failed to load carriers",
        description: err?.message || "Unable to fetch carriers.",
        variant: "destructive",
      });
      setWbCarriers([]);
      return [];
    } finally {
      setLoadingCarriers(false);
    }
  };

  const fetchWbPlans = async (carrier: any, stateOverride?: string, dateOverride?: string): Promise<any[]> => {
    setLoadingPlans(true);
    setWbPlans([]);
    setAreaRates([]);
    setPreviewOpen(false);
    setPreviewPlan(null);

    try {
      const carrierId =
        carrier?.carrierId || carrier?.id || carrier?.CarrierId || carrier?.CarrierID;
      if (!carrierId) throw new Error("Carrier ID not found");

      const response = await apiRequest(
        "GET",
        `/api/plan-source/plans?carrierId=${carrierId}&state=${stateOverride ?? selectedState}&effectiveDate=${dateOverride ?? effectiveDate}`,
        undefined
      );
      const data = await response.json();
      if (data?.success && Array.isArray(data?.plans)) {
        setWbPlans(data.plans);
        return data.plans;
      }
      throw new Error(data?.message || "Invalid plans response");
    } catch (err: any) {
      toast({
        title: "Failed to load plans",
        description: err?.message || "Unable to fetch plans.",
        variant: "destructive",
      });
      setWbPlans([]);
      return [];
    } finally {
      setLoadingPlans(false);
    }
  };

  const openPlanPreview = async (
    plan: any,
    opts?: {
      carrierIdOverride?: string | number;
      initialRatingAreaId?: number;
      stateOverride?: string;
      dateOverride?: string;
    },
  ) => {
    setPreviewOpen(true);
    setPreviewPlan(plan);
    setPreviewLoading(true);
    setPreviewError(null);
    setAreaRates([]);
    setSelectedAreaIdx(0);

    try {
      const carrierId =
        opts?.carrierIdOverride ||
        selectedCarrier?.carrierId ||
        selectedCarrier?.id ||
        selectedCarrier?.CarrierId ||
        selectedCarrier?.CarrierID;
      const planId =
        plan?.NQSPlanId || plan?.OrionPlanId || plan?.planId || plan?.id;
      if (!carrierId || !planId) throw new Error("Missing carrierId or planId");

      const res = await fetch(
        `/api/plan-source/plan-details?carrierId=${carrierId}&state=${opts?.stateOverride ?? selectedState}&effectiveDate=${opts?.dateOverride ?? effectiveDate}&planId=${planId}&include=BenefitsAndRates`,
        { credentials: "include" }
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to fetch plan details");
      }

      const json = await res.json();
      const data = json?.data;
      const normalized = normalizeAreaRatesFromPlanDetails(data);
      if (normalized.length === 0) {
        throw new Error("No rates found for this plan");
      }

      setAreaRates(normalized);
      if (opts?.initialRatingAreaId != null) {
        const idx = normalized.findIndex(
          (a) => mapWordBrownAreaLabelToId(a.label) === opts.initialRatingAreaId,
        );
        if (idx >= 0) setSelectedAreaIdx(idx);
      }
    } catch (err: any) {
      setPreviewError(err?.message || "Unable to fetch plan details.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleImport = () => {
    const selected = areaRates[selectedAreaIdx];
    if (!selected) return;
    const mapped = mapRatesToAgeBandRates(selected.rates);
    if (Object.keys(mapped).length === 0) {
      toast({
        title: "No rates to import",
        description: "This plan does not have usable age band rates.",
        variant: "destructive",
      });
      return;
    }

    const ratingAreaLabel = selected.label;
    const ratingAreaId = mapWordBrownAreaLabelToId(ratingAreaLabel) || undefined;

    onImport({
      ageBandRates: mapped,
      ratingAreaId,
      ratingAreaLabel,
      effectiveStart: effectiveDate,
    });

    onOpenChange(false);
  };

  const reset = () => {
    setWbCarriers([]);
    setSelectedCarrier(null);
    setWbPlans([]);
    setPlanSearchTerm("");
    setAreaRates([]);
    setSelectedAreaIdx(0);
    setPreviewOpen(false);
    setPreviewPlan(null);
    setPreviewLoading(false);
    setPreviewError(null);
  };

  // Auto-fill from the plan already open behind this dialog, instead of
  // making the broker retype the state/carrier/plan/date they're already
  // looking at (found 2026-09-22: this was the whole reason someone typed
  // the wrong effective date and hit the cache-miss bug above). This
  // component stays mounted across opens (PlanRates.tsx toggles `open`, not
  // the component instance), so useState's initial values only apply once -
  // this effect is what re-syncs state on every fresh open. Guarded by a ref
  // so it runs once per open, not on every render while open.
  const autoFilledRef = useRef(false);
  // WHY not opening the Plan Preview automatically: this is a two-step flow
  // on purpose - step 1 (state/carrier/plan) should be verifiable and
  // changeable before committing to step 2, which is what actually shows
  // rates and can be imported. Auto-opening the preview would skip that
  // check. The area to pre-select in step 2 is stashed here and read by the
  // "Select" button below, once the broker chooses to proceed.
  const pendingRatingAreaIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!open) {
      autoFilledRef.current = false;
      return;
    }
    if (autoFilledRef.current) return;
    autoFilledRef.current = true;
    pendingRatingAreaIdRef.current = initialRatingAreaId;

    const state = initialState || "CA";
    const date = initialEffectiveDate || today;
    setSelectedState(state);
    setEffectiveDate(date);

    if (initialCarrierId == null && initialPlanId == null) return;

    (async () => {
      const carrierList = await fetchWbCarriers(state, date);
      const matchedCarrier =
        initialCarrierId != null
          ? carrierList.find((c: any) => Number(c.id) === Number(initialCarrierId))
          : null;
      if (!matchedCarrier) return;
      setSelectedCarrier(matchedCarrier);

      const planList = await fetchWbPlans(matchedCarrier, state, date);
      const matchedPlan =
        initialPlanId != null
          ? planList.find((p: any) => Number(p.planId) === Number(initialPlanId))
          : null;
      if (!matchedPlan) return;

      // Filter the list down to the matched plan instead of opening its
      // preview - lets the broker verify (or change) the pre-filled data
      // before clicking "Select" themselves.
      setPlanSearchTerm(matchedPlan.name || matchedPlan.planName || "");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import age band rates from library</DialogTitle>
        </DialogHeader>

        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium">State</label>
                <Select
                  value={selectedState}
                  onValueChange={(value) => {
                    setSelectedState(value);
                    reset();
                  }}
                >
                  <SelectTrigger className="h-10 mt-1">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state.code} value={state.code}>
                        {state.name} ({state.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium">Effective Date</label>
                <Input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => {
                    setEffectiveDate(e.target.value);
                    reset();
                  }}
                  className="h-10 mt-1"
                />
              </div>

              <div className="flex items-end">
                <Button
                  onClick={() => fetchWbCarriers()}
                  disabled={loadingCarriers}
                  className="w-full h-10"
                >
                  {loadingCarriers ? "Loading carriers..." : "Find Carriers"}
                </Button>
              </div>
            </div>

            {wbCarriers.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Carrier</label>
                  <Select
                    value={
                      selectedCarrier
                        ? (
                            selectedCarrier.carrierId ||
                            selectedCarrier.id ||
                            selectedCarrier.CarrierId ||
                            selectedCarrier.CarrierID
                          )?.toString()
                        : undefined
                    }
                    onValueChange={(val) => {
                      const carrier = wbCarriers.find((c: any) =>
                        [c.carrierId, c.id, c.CarrierId, c.CarrierID]
                          .map((x: any) => (x != null ? String(x) : ""))
                          .includes(val)
                      );
                      setSelectedCarrier(carrier || null);
                      if (carrier) fetchWbPlans(carrier);
                    }}
                  >
                    <SelectTrigger className="h-10 mt-1">
                      <SelectValue placeholder="Select carrier" />
                    </SelectTrigger>
                    <SelectContent>
                      {wbCarriers.map((carrier: any, idx: number) => {
                        const id =
                          carrier.carrierId ||
                          carrier.id ||
                          carrier.CarrierId ||
                          carrier.CarrierID;
                        const name =
                          carrier.carrierName ||
                          carrier.name ||
                          carrier.CarrierName ||
                          "Unknown Carrier";
                        return (
                          <SelectItem key={id || idx} value={String(id)}>
                            {name}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button
                    variant="outline"
                    className="h-10 w-full"
                    onClick={() => selectedCarrier && fetchWbPlans(selectedCarrier)}
                    disabled={!selectedCarrier || loadingPlans}
                  >
                    {loadingPlans ? "Loading plans..." : "Reload Plans"}
                  </Button>
                </div>
              </div>
            )}

            {selectedCarrier && (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search plans..."
                    value={planSearchTerm}
                    onChange={(e) => setPlanSearchTerm(e.target.value)}
                    className="pl-10 h-10"
                  />
                </div>

                {loadingPlans ? (
                  <div className="text-center py-6 text-sm text-muted-foreground">
                    Loading plans...
                  </div>
                ) : filteredPlans.length > 0 ? (
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {filteredPlans.map((plan: any, index: number) => (
                      <div
                        key={plan.wordBrownId || plan.id || index}
                        className="flex items-center justify-between border rounded-md p-3 hover:bg-muted/50"
                      >
                        <div className="min-w-0">
                          <div className="font-medium truncate">
                            {plan.name || "Unnamed Plan"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {plan.planCode ? `Code: ${plan.planCode}` : ""}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="ml-3"
                          onClick={() =>
                            openPlanPreview(plan, { initialRatingAreaId: pendingRatingAreaIdRef.current })
                          }
                          disabled={loadingDetails}
                        >
                          {loadingDetails ? "Loading..." : "Select"}
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-sm text-muted-foreground">
                    No plans found
                  </div>
                )}
              </div>
            )}

            {areaRates.length > 0 && (
              <div className="text-sm text-muted-foreground">
                Select a plan to preview its rate areas and rates.
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Plan Preview</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <div className="font-medium">{previewPlan?.name || "Unnamed Plan"}</div>
                {previewPlan?.planCode ? (
                  <div className="text-xs text-muted-foreground">
                    Code: {previewPlan.planCode}
                  </div>
                ) : null}
              </div>

              {previewLoading ? (
                <div className="text-sm text-muted-foreground">Loading rates…</div>
              ) : previewError ? (
                <div className="text-sm text-destructive">{previewError}</div>
              ) : areaRates.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                    <div>
                      <label className="text-sm font-medium">Rate area</label>
                      <Select
                        value={String(selectedAreaIdx)}
                        onValueChange={(val) => setSelectedAreaIdx(Number(val))}
                      >
                        <SelectTrigger className="h-10 mt-1">
                          <SelectValue placeholder="Select rate area" />
                        </SelectTrigger>
                        <SelectContent>
                          {areaRates.map((a, idx) => (
                            <SelectItem key={a.label + idx} value={String(idx)}>
                              {a.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setPreviewOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleImport}>
                        Import rates
                      </Button>
                    </div>
                  </div>

                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Age</TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(areaRates[selectedAreaIdx]?.rates || []).slice(0, 200).map((r: any, idx: number) => {
                          const ageLabel = formatAgeLabel(r);
                          const premium = formatPremium(r);
                          if (!ageLabel || !premium) return null;
                          return (
                            <TableRow key={`${ageLabel}:${idx}`}>
                              <TableCell>{ageLabel}</TableCell>
                              <TableCell className="text-right">${premium}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No rates available for this plan.
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
